<template>
    <el-dialog
        :visible="visible"
        :modal-append-to-body="false"
        :close-on-click-modal="false"
        append-to-body
        class="outsourcing-person-import-dialog ah-detail-dialog"
        :width="parsedRows.length > 0 ? null : '400px'"
        top="10vh"
        :before-close="() => onClose()"
        :show-close="false"
        @open="onOpen"
    >
        <template slot="title">批次匯入委外勞務</template>

        <div class="detail-row">
            <!-- 上傳區 -->
            <div v-if="parsedRows.length === 0" class="upload-area">
                <el-button type="primary" class="upload-btn" @click="$refs.fileInput.click()">上傳 Excel 檔案</el-button>
                <div class="hint">請使用下方「下載匯入範例」所提供的標準格式。</div>
                <div>
                    <el-button @click="onDownloadTemplate">下載匯入範例</el-button>
                    <el-button plain @click="onClose()">取消</el-button>
                </div>
            </div>

            <!-- 預覽表格 -->
            <template v-else>
                <el-alert v-if="warningCount > 0" type="warning" :closable="false" style="margin-bottom: 12px">
                    共 {{ warningCount }} 筆資料有預檢警告，匯入時可能失敗
                </el-alert>
                <el-table :data="parsedRows" border style="width: 100%" height="300px" empty-text="沒有資料">
                    <el-table-column type="index" label="#" width="50" fixed />
                    <el-table-column label="匯入狀態" width="120" fixed>
                        <template slot-scope="{ row }">
                            <span :class="statusClass(row.importStatus)">{{ statusLabel(row.importStatus) }}</span>
                        </template>
                    </el-table-column>
                    <el-table-column label="預檢" width="140" show-overflow-tooltip>
                        <template slot-scope="{ row }">
                            <span v-if="row._warning" style="color: #E6A23C">{{ row._warning }}</span>
                            <span v-else style="color: #67C23A">OK</span>
                        </template>
                    </el-table-column>
                    <el-table-column label="姓名" prop="_fnam" width="120" show-overflow-tooltip />
                    <el-table-column label="類型" prop="_catName" width="120" />
                    <el-table-column label="國籍" prop="_abd" width="160" />
                    <el-table-column label="證件編號" prop="_bsn" width="120" />
                    <el-table-column label="帳戶數" prop="_bankCount" width="80" />
                </el-table>
            </template>
        </div>

        <!-- 底部按鈕 -->
        <div class="detail-row footer right" v-if="parsedRows.length > 0">
            <div style="display: flex; justify-content: space-between; align-items: center;">
                <template v-if="importStatus === 'init'">
                    <div>
                        <el-button plain @click="onReset">重新上傳</el-button>
                        <el-button plain @click="onClose()">取消</el-button>
                    </div>
                    <div>
                        <el-button type="primary" @click="onImport">開始匯入 {{ parsedRows.length }} 筆資料</el-button>
                    </div>
                </template>
                <template v-else>
                    <div style="flex: 0.9">
                        <el-progress :percentage="progress" :color="progress === 100 ? '#92DE56' : '#463cb2'" />
                    </div>
                    <div v-if="importStatus === 'importing'">
                        <el-button :loading="isStopping" plain @click="isStopping = true">
                            {{ isStopping ? '停止中' : '停止' }}
                        </el-button>
                    </div>
                    <div v-else>
                        <el-button type="primary" @click="onClose(true)">完成</el-button>
                    </div>
                </template>
            </div>
        </div>

        <input v-show="false" ref="fileInput" type="file" accept=".xlsx" @change="onFileChange" />
    </el-dialog>
</template>

<script>
import XLSX from 'xlsx';
import ExcelJS from 'exceljs';

const MAX_BANKS = 3;

const ABD_MAP = { 0: '本國人', 1: '外國籍（已滿183天）', 2: '外國籍（未滿183天）' };
const ABD_REVERSE = { '本國人': 0, '外國籍（已滿183天）': 1, '外國籍（未滿183天）': 2 };

const SECTION_COLORS = { basic: 'FFE2EFDA', bank: 'FFFFF2CC' };

function buildColumnLayout() {
    const columns = [
        { header: '系統編號',         key: 'voo',                  width: 14, section: 'basic', hint: '新增時不需填寫' },
        { header: '類型',             key: 'caT_NAM',              width: 20, section: 'basic', required: true, hint: '必須在「委外類型」中已建立' },
        { header: '姓名',             key: 'fnam',                 width: 20, section: 'basic', required: true },
        { header: '稱謂',             key: '_gen',                 width: 10, section: 'basic', required: true, hint: '先生/小姐' },
        { header: '國籍',             key: '_abd',                 width: 30, section: 'basic', required: true, hint: '本國人/外國籍（已滿183天）/外國籍（未滿183天）' },
        { header: '證件類型',         key: '_idType',              width: 22, section: 'basic', required: true, hint: '本國：身分證/營業統一編號\n外國：護照號碼/居留證' },
        { header: '證件編號',         key: 'bsn',                  width: 16, section: 'basic', required: true, text: true },
        { header: '戶籍地區／縣市',   key: '_permanentCityOrArea', width: 16, section: 'basic' },
        { header: '戶籍地址',         key: 'permanentAddr',        width: 40, section: 'basic' },
        { header: '戶籍地址郵遞區號', key: 'permanentAdR_CODE',    width: 14, section: 'basic', text: true },
        { header: '聯絡地區／縣市',   key: '_cityOrArea',          width: 16, section: 'basic' },
        { header: '聯絡地址',         key: 'adr',                  width: 40, section: 'basic' },
        { header: '聯絡地址郵遞區號', key: 'adR_CODE',             width: 14, section: 'basic', text: true },
        { header: '投保職業工會',     key: '_oS_GRP',              width: 14, section: 'basic', required: true, hint: '有/無' },
        { header: '電話',             key: '_tel',                 width: 16, section: 'basic', text: true },
        { header: '分機',             key: '_ext',                 width: 10, section: 'basic', text: true },
        { header: 'E-mail',           key: '_eml',                 width: 30, section: 'basic' },
        { header: '備註',             key: 'note',                 width: 40, section: 'basic' },
    ];

    for (let n = 1; n <= MAX_BANKS; n++) {
        columns.push(
            { header: `帳戶${n}_類型`,         key: `b${n}_type`,   width: 14, section: 'bank', hint: '本國帳戶/外國帳戶' },
            { header: `帳戶${n}_戶名`,         key: `b${n}_tpc`,    width: 20, section: 'bank' },
            { header: `帳戶${n}_銀行代號`,     key: `b${n}_boo`,    width: 14, section: 'bank', text: true, hint: '本國帳戶用，共3碼' },
            { header: `帳戶${n}_分行代號`,     key: `b${n}_doo`,    width: 14, section: 'bank', text: true, hint: '本國帳戶用，共7碼' },
            { header: `帳戶${n}_收款帳號`,     key: `b${n}_acc`,    width: 22, section: 'bank', text: true },
            { header: `帳戶${n}_銀行英文名稱`, key: `b${n}_abroad`, width: 24, section: 'bank', hint: '外國帳戶必填' },
            { header: `帳戶${n}_SWIFT Code`,   key: `b${n}_swift`,  width: 16, section: 'bank', hint: '外國帳戶必填' },
            { header: `帳戶${n}_備註`,         key: `b${n}_note`,   width: 20, section: 'bank' },
        );
    }
    return columns;
}

// 產生 Excel 標題文字：必填加 * 前綴，有 hint 加換行註解
function buildHeaderText(col) {
    let text = col.required ? `*${col.header}` : col.header;
    if (col.hint) text += `\r\n(${col.hint})`;
    return text;
}

// 正規化匯入的 key：去除 * 前後綴、取 \r\n 前的第一行
function normalizeKey(key) {
    return key.replace(/^\*|\*$/g, '').split(/\r?\n/)[0].trim();
}

export default {
    name: 'OutsourcingPersonImportDialog',
    props: {
        visible: Boolean,
    },
    data() {
        return {
            parsedRows: [],
            importStatus: 'init',
            isStopping: false,
            progress: 0,
            options: [],
        };
    },
    computed: {
        warningCount() {
            return this.parsedRows.filter(r => r._warning).length;
        },
    },
    methods: {
        onOpen() {
            this.parsedRows = [];
            this.importStatus = 'init';
            this.isStopping = false;
            this.progress = 0;
            this.fetchOptions();
        },
        onReset() {
            this.parsedRows = [];
            this.importStatus = 'init';
            this.progress = 0;
            if (this.$refs.fileInput) this.$refs.fileInput.value = '';
        },
        onClose(refresh) {
            this.$emit('close', refresh);
        },
        async fetchOptions() {
            const resp = await this.$api({ url: '/Tool/GetDepartmentAndVendorSettings', method: 'GET' });
            this.options = resp.data.vendorSettings_OS;
        },

        // ── 下載匯入範例 ──────────────────────────────────────────
        async onDownloadTemplate() {
            const columns = buildColumnLayout();
            const workbook = new ExcelJS.Workbook();
            const worksheet = workbook.addWorksheet('import');

            // 先用 clean header 建立欄位（供 addRow 的 key 對應）
            worksheet.columns = columns.map(col => ({ header: col.header, key: col.key, width: col.width }));

            // 覆寫標題列：加上 * 必填前綴和換行註解
            const headerRow = worksheet.getRow(1);
            columns.forEach((col, idx) => {
                const cell = headerRow.getCell(idx + 1);
                cell.value = buildHeaderText(col);
                cell.font = { bold: true, size: 10 };
                cell.alignment = { wrapText: true, vertical: 'top' };
                cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: SECTION_COLORS[col.section] } };
            });
            headerRow.height = 48;

            // 範例資料：本國人 + 外國人
            worksheet.addRow({
                voo: '', caT_NAM: '音樂製作', fnam: '陳示範', _gen: '先生',
                _abd: '本國人', _idType: '身分證', bsn: 'A111111111',
                _permanentCityOrArea: '臺北市', permanentAddr: '中正區林森北路一段48號', permanentAdR_CODE: '105',
                _cityOrArea: '臺北市', adr: '中正區林森北路一段48號', adR_CODE: '105',
                _oS_GRP: '無', _tel: '0212344321', _ext: '777', _eml: 'test@mail.com', note: '',
                b1_type: '本國帳戶', b1_tpc: '陳示範', b1_boo: '004', b1_doo: '0040037', b1_acc: '12344321', b1_abroad: '', b1_swift: '', b1_note: '',
            });
            worksheet.addRow({
                voo: '', caT_NAM: '導演', fnam: 'Amy', _gen: '小姐',
                _abd: '外國籍（已滿183天）', _idType: '護照號碼', bsn: 'A222222222',
                _permanentCityOrArea: '美國', permanentAddr: '45 Rockefeller Plaza, New York, NY 10111, U.S.', permanentAdR_CODE: '10111',
                _cityOrArea: '美國', adr: '45 Rockefeller Plaza, New York, NY 10111, U.S.', adR_CODE: '10111',
                _oS_GRP: '無', _tel: '+12125888601', _ext: '', _eml: 'test@mail.com', note: '預計10/31開始外包',
                b1_type: '外國帳戶', b1_tpc: 'Amy', b1_boo: '', b1_doo: '', b1_acc: '111111111', b1_abroad: 'Bank of America', b1_swift: '654321', b1_note: '',
            });

            // 資料列文字格式（防數字轉換）
            const textCols = columns.reduce((acc, col, idx) => { if (col.text) acc.push(idx + 1); return acc; }, []);
            worksheet.eachRow((row, rowNum) => {
                if (rowNum === 1) return;
                textCols.forEach(colNum => {
                    const cell = row.getCell(colNum);
                    if (cell.value != null && cell.value !== '') cell.numFmt = '@';
                });
            });

            worksheet.views = [{ state: 'frozen', ySplit: 1 }];

            const buffer = await workbook.xlsx.writeBuffer();
            const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = '委外勞務匯入範例_新版.xlsx';
            a.click();
            URL.revokeObjectURL(url);
        },

        // ── 檔案解析 ──────────────────────────────────────────────
        onFileChange(event) {
            const file = event.target.files[0];
            if (!file) return;
            event.target.value = '';

            const reader = new FileReader();
            reader.onload = (e) => {
                try {
                    const wb = XLSX.read(e.target.result, { type: 'array' });
                    if (!wb.Sheets.import) throw new Error('找不到工作表「import」，請確認檔案為委外勞務匯出的 Excel');

                    const rawSheet = XLSX.utils.sheet_to_json(wb.Sheets.import, { defval: '' });
                    if (rawSheet.length === 0) throw new Error('Excel 內沒有資料列');

                    // 正規化 key：去除 * 前後綴，取 \r\n 前的第一行（相容帶註解的標題）
                    const raw = rawSheet.map(row => {
                        const cleaned = {};
                        Object.keys(row).forEach(k => { cleaned[normalizeKey(k)] = row[k]; });
                        return cleaned;
                    });

                    this.parsedRows = raw.map((row) => {
                        const banks = this.parseBanks(row);
                        const fnam = row['姓名*'] || row['姓名'] || '';
                        const catName = row['類型'] || '';
                        const abdText = row['國籍'] || '';
                        const warning = this.preValidate(row, fnam, catName, abdText);

                        return {
                            _raw: row,
                            _fnam: fnam,
                            _catName: catName,
                            _abd: abdText,
                            _bsn: row['證件編號'] ? String(row['證件編號']) : '',
                            _bankCount: banks.length,
                            _warning: warning,
                            importStatus: 'init',
                        };
                    });
                } catch (err) {
                    this.$message({ showClose: true, message: err.message || '檔案解析失敗', type: 'error' });
                }
            };
            reader.readAsArrayBuffer(file);
        },

        // ── 預檢驗證 ──────────────────────────────────────────────
        preValidate(row, fnam, catName, abdText) {
            const warnings = [];
            if (!fnam) warnings.push('缺姓名');
            if (!catName) warnings.push('缺類型');
            if (catName && !this.options.find(o => o.catnam === catName)) warnings.push('類型無效');
            if (!abdText) warnings.push('缺國籍');
            else if (!(abdText in ABD_REVERSE)) warnings.push('國籍無效');
            if (!row['證件類型']) warnings.push('缺證件類型');
            if (!row['證件編號']) warnings.push('缺證件編號');
            return warnings.length > 0 ? warnings.join('、') : '';
        },

        // ── 解析收款帳戶 ──────────────────────────────────────────
        parseBanks(row) {
            const banks = [];
            for (let i = 1; i <= MAX_BANKS; i++) {
                const tpc = row[`帳戶${i}_戶名`];
                const acc = row[`帳戶${i}_收款帳號`];
                if (!tpc && !acc) continue;
                const accountType = row[`帳戶${i}_類型`] === '外國帳戶' ? 2 : 1;
                const bank = {
                    accountType,
                    bnK_TPC: tpc || '',
                    bnK_ACC: acc ? String(acc) : '',
                    bnK_BOO: null,
                    bnK_DOO: null,
                    abroadBankName: null,
                    swiftCode: null,
                    note: row[`帳戶${i}_備註`] || '',
                };
                if (accountType === 1) {
                    bank.bnK_BOO = row[`帳戶${i}_銀行代號`] ? String(row[`帳戶${i}_銀行代號`]) : null;
                    bank.bnK_DOO = row[`帳戶${i}_分行代號`] ? String(row[`帳戶${i}_分行代號`]) : null;
                } else {
                    bank.abroadBankName = row[`帳戶${i}_銀行英文名稱`] || '';
                    bank.swiftCode = row[`帳戶${i}_SWIFT Code`] ? String(row[`帳戶${i}_SWIFT Code`]) : '';
                }
                banks.push(bank);
            }
            return banks;
        },

        // ── 組裝 API payload ──────────────────────────────────────
        buildPayload(raw) {
            const category = this.options.find(o => o.catnam === raw['類型']);
            if (!category) throw new Error(`無效的委外勞務類型：「${raw['類型']}」`);

            const fnam = raw['姓名*'] || raw['姓名'] || '';
            if (!fnam) throw new Error('缺少姓名');

            const abdText = raw['國籍'] || '';
            const abd = ABD_REVERSE[abdText];
            if (abd === undefined) throw new Error(`無效的國籍：「${abdText}」`);

            const gen = raw['稱謂'] === '小姐' ? 1 : 0;
            const idType = raw['證件類型'] || '';

            const payload = {
                vdR_TYP: 'OS',
                caT_SNO: category.sno,
                fnam,
                bsn: raw['證件編號'] ? String(raw['證件編號']) : '',
                abd,
                abD_TYP: null,
                profitType: 0,
                identificationType: 0,
                bsN_TYP: 0,
                ara: null,
                cty: null,
                permanentAddrAra: null,
                permanentAddrCty: null,
                adr: raw['聯絡地址'] || '',
                adR_CODE: raw['聯絡地址郵遞區號'] ? String(raw['聯絡地址郵遞區號']) : '',
                permanentAddr: raw['戶籍地址'] || '',
                permanentAdR_CODE: raw['戶籍地址郵遞區號'] ? String(raw['戶籍地址郵遞區號']) : '',
                note: raw['備註'] || '',
                onf: 1,
                oS_GRP: raw['投保職業工會'] === '有',
                oS_GRP_FILE: '',
                vendorContacts: [{
                    nam: '',
                    gen,
                    job: '',
                    tel: raw['電話'] ? String(raw['電話']) : '',
                    ext: raw['分機'] ? String(raw['分機']) : '',
                    eml: raw['E-mail'] || '',
                    dep: '',
                }],
                vendorBanks: this.parseBanks(raw),
            };

            if (abd === 0) {
                // 本國人
                payload.bsN_TYP = idType === '營業統一編號' ? 2 : 1;
                payload.cty = raw['聯絡地區／縣市'] || '';
                payload.permanentAddrCty = raw['戶籍地區／縣市'] || '';
                payload.oS_GRP = raw['投保職業工會'] === '有';
            } else {
                // 外國人
                payload.identificationType = idType === '居留證' ? 2 : 1;
                payload.abD_TYP = 2;
                payload.ara = raw['聯絡地區／縣市'] || '';
                payload.permanentAddrAra = raw['戶籍地區／縣市'] || '';
            }

            if (raw['系統編號']) payload.voo = String(raw['系統編號']);

            return payload;
        },

        // ── 執行匯入 ─────────────────────────────────────────────
        async onImport() {
            this.importStatus = 'importing';
            const total = this.parsedRows.length;
            let done = 0;

            for (const row of this.parsedRows) {
                if (this.isStopping) break;
                row.importStatus = 'importing';
                done++;
                this.progress = Math.round((done / total) * 100);

                try {
                    const payload = this.buildPayload(row._raw);
                    // eslint-disable-next-line no-await-in-loop
                    await this.$api_silent({
                        url: 'Outsourcing/UpsertOutsourcingDetail',
                        data: payload,
                        method: 'POST',
                    });
                    row.importStatus = '已新增';
                } catch (err) {
                    if (err.response) {
                        row.importStatus = err.response.status === 400
                            ? '缺少欄位'
                            : (err.response.data || '伺服器錯誤');
                    } else {
                        row.importStatus = err.message || err.msg || '未知錯誤';
                    }
                }
            }

            this.importStatus = 'complete';
        },

        // ── 狀態顯示 ─────────────────────────────────────────────
        statusLabel(s) {
            if (s === 'init') return '---';
            if (s === 'importing') return '匯入中...';
            return s;
        },
        statusClass(s) {
            if (s === '已新增') return 'status-ok';
            if (s === 'init' || s === 'importing') return '';
            return 'status-err';
        },
    },
};
</script>

<style lang="scss" scoped>
.upload-area {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 16px;
    .upload-btn {
        width: 260px;
    }
    .hint {
        font-size: 13px;
        color: #888;
        text-align: center;
    }
}
.status-ok {
    color: #44bf00;
    &::before { content: 'Ｏ'; }
}
.status-err {
    color: red;
    &::before { content: 'Ｘ '; }
}
</style>

<style lang="scss">
.outsourcing-person-import-dialog.ah-detail-dialog {
    .detail-row {
        padding: 40px;
    }
}
</style>
